// @ts-nocheck
/** *****
 * Store Custom Code:
 * For updates, compare with: modules\content-library\src\Widgets\Footer\FooterContainer.tsx
 * ****** */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { StyledClickable } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { storeLogoStyles } from "@store/Themes/common/mixins/logo";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import merge from "lodash/merge";

const enum fields {
    firstTitle = "firstTitle",
    secondTitle = "secondTitle",
    thirdTitle = "thirdTitle",
}
/* Store Custom Code - End */

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
    // Store Custom Code
    fields: {
        [fields.firstTitle]: string;
        [fields.secondTitle]: string;
        [fields.thirdTitle]: string;
    };
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // Store Custom Code
    footerContainerWrapper?: InjectableCss;
    widgetContainerWrapper?: InjectableCss;
    logoInfoGridContainer?: GridContainerProps;
    logoInfoGridItem?: GridItemProps;
    contactInfoWrapper?: InjectableCss;
    linkListGridItem?: GridItemProps;
    headerStyles?: TypographyPresentationProps;
    legalContainerWrapper?: InjectableCss;
    legalGridContainer?: GridContainerProps;
    legalLinksGridItem?: GridItemProps;
}

/* Store Custom Code - Start */
const mobileWrapperStyles = () => {
    return css`
        ${x => siteWidthStyles(merge([], x.theme.design.layout.spacing.side, [pxToRem(50)]))}
    `;
};

const linkListGridItemStyles: GridItemProps = {
    width: [12, 4, 4, 4, 4],
    css: css`
        flex-direction: column;

        ${StyledClickable} {
            ${linkStyles("common.accentContrast", "text.linkHover", false, true)}
        }

        li {
            ${spacingStyles(null, "0.25rem")}
        }
    `,
};
/* Store Custom Code - End */

export const footerContainerStyles: FooterContainerStyles = {
    /* Store Custom Code - Start */
    footerContainerWrapper: {
        css: css`
            background: ${getColor("common.accent")};
            border-top: 8px solid ${getColor("secondary.light")};
            ${x => breakpointValues(merge([], x.theme.design.layout.spacing.side, [pxToRem(30)]), "padding-top")}
            ${x => breakpointValues([x.theme.design.layout.spacing.side[0], "1rem"], "padding-bottom")}
        `,
    },
    widgetContainerWrapper: {
        css: css`
            ${siteWidthStyles()}
        `,
    },
    logoWrapper: {
        css: css`
            ${storeLogoStyles()}
            line-height: ${x => x.theme.design.typography.lineHeight.smaller};
        `,
    },
    logoGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    logoInfoGridContainer: {
        gap: 20,
        offsetCss: css`
            flex-direction: column;
        `,
    },
    logoInfoGridItem: {
        width: 12,
    },
    contactInfoWrapper: {
        css: css`
            color: ${getColor("text.accent")};
            font-weight: ${x => x.theme.design.typography.weight.medium};

            ${StyledClickable} {
                ${linkStyles("text.accent", "text.linkHover", false, true)}
            }

            ${breakpoint("xs", "only")} {
                text-align: center;
            }
        `,
    },
    headerStyles: {
        forwardAs: "h2",
        color: "primary.light",
        css: css`
            margin-bottom: 0.75rem;
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.medium};
            text-transform: uppercase;
        `,
    },
    linkListGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    linkList1GridItem: linkListGridItemStyles,
    linkList2GridItem: linkListGridItemStyles,
    linkList3GridItem: linkListGridItemStyles,
    legalContainerWrapper: {
        css: css`
            ${mobileWrapperStyles()};
            padding-top: 2.1rem;
        `,
    },
    legalGridContainer: {
        gap: 20,
        offsetCss: css`
            justify-content: center;
        `,
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};

            ${StyledClickable} {
                ${linkStyles("text", "text.linkHover", false, true)}
            }
        `,
    },
    legalLinksGridItem: {
        width: 12,
        css: css`
            flex-basis: auto;

            ${breakpoint("xs", "only")} {
                width: 100%;
                ul {
                    display: flex;
                    justify-content: space-between;
                }
            }
        `,
    },
    richContentGridItem: {
        width: 12,
        css: css`
            flex-basis: auto;

            ${breakpoint("xs", "only")} {
                span {
                    display: block;
                    text-align: center;
                }
            }
        `,
    },
    /* Store Custom Code - End */
};

const FooterContainer: FC<OwnProps> = ({
    id,
    extendedStyles,
    // Store Custom Code
    fields,
}) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    return (
        /* Store Custom Code - Start */
        <StyledWrapper {...styles.footerContainerWrapper}>
            <StyledWrapper {...styles.widgetContainerWrapper}>
                <GridContainer>
                    <GridItem {...styles.logoGridItem}>
                        <GridContainer {...styles.logoInfoGridContainer}>
                            <GridItem {...styles.logoInfoGridItem}>
                                <StyledWrapper {...styles.logoWrapper}>
                                    <Zone zoneName="Logo" contentId={id} />
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.logoInfoGridItem}>
                                <StyledWrapper {...styles.contactInfoWrapper}>
                                    <Zone zoneName="Contact" contentId={id} />
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.linkListGridItem}>
                        <GridContainer>
                            <GridItem {...styles.linkList1GridItem}>
                                <Typography {...styles.headerStyles}>{fields.firstTitle}</Typography>
                                <Zone zoneName="LinkList1" contentId={id} />
                            </GridItem>
                            <GridItem {...styles.linkList2GridItem}>
                                <Typography {...styles.headerStyles}>{fields.secondTitle}</Typography>
                                <Zone zoneName="LinkList2" contentId={id} />
                            </GridItem>
                            <GridItem {...styles.linkList3GridItem}>
                                <Typography {...styles.headerStyles}>{fields.thirdTitle}</Typography>
                                <Zone zoneName="LinkList3" contentId={id} />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
            <StyledWrapper {...styles.legalContainerWrapper}>
                <GridContainer {...styles.legalGridContainer}>
                    <GridItem {...styles.richContentGridItem}>
                        <Zone zoneName="RichContent" contentId={id} />
                    </GridItem>
                    <GridItem {...styles.legalLinksGridItem}>
                        <Zone zoneName="Links" contentId={id} />
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
        /* Store Custom Code - End */
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
        /* Store Custom Code - Start */
        fieldDefinitions: [
            {
                name: fields.firstTitle,
                displayName: "First Column Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.secondTitle,
                displayName: "Second Column Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.thirdTitle,
                displayName: "Third Column Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
        /* Store Custom Code - End */
    },
};

export default footerContainer;
